import { AccordionWithChildCount, Card, Text } from '@payaca/ambrosia';
import RouterTabs, { TabPanel } from '@payaca/components/routerTabs/RouterTabs';
import { useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

import ConfirmPaymentModal from '@/ui/components/confirmPaymentModal/ConfirmPaymentModal';
import DealPayments from '@/ui/components/dealPayments/DealPayments';
import DealScheduledEvents from '@/ui/components/dealScheduledEvents/DealScheduledEvents';
import PageBanner from '@/ui/components/pageBanner/PageBanner';
import { getBacsPendingPayments } from '@payaca/helpers/jobPaymentHelper';

import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as documentActions from '@payaca/store/documents/documentActions';
import { useDeal } from '@payaca/store/hooks/appState';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobPaymentActions from '@payaca/store/jobPayments/jobPaymentsActions';
import * as jobActions from '@payaca/store/jobs/jobsActions';
import * as notesActions from '@payaca/store/notes/notesActions';
import * as pipelineActions from '@payaca/store/pipelines/pipelineActions';
import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';
import { Helmet } from 'react-helmet';
import { actions as usersActions } from '../../../api/users';

import { getJobsByDealId } from '@/utils/stateAccessors';

import { DealProposalsInvoices as DealProposalsInvoicesV1 } from '@/ui/components/dealProposalsInvoices/DealProposalsInvoices.v1';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';

import useGetMe from '@/api/queries/me/useGetMe';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import { Contact } from '@/gql/graphql';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslation } from '@/i18n';
import { DealInvoicesPayments } from '@/ui/components/dealInvoicesPayments/DealInvoicesPayments';
import { DealProposals } from '@/ui/components/dealProposals/DealProposals';
import { DealTasks } from '@/ui/components/dealTasks/DealTasks';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import SendIntegratedEmailModal from '@/ui/components/sendIntegratedEmailModal/SendIntegratedEmailModal';
import ActivityPanel from '@/ui/pages/projectPage/components/ActivityPanel';
import CommitmentsPanel from '@/ui/pages/projectPage/components/CommitmentsPanel';
import ContactCard from '@/ui/pages/projectPage/components/ContactCard';
import FilesPanel from '@/ui/pages/projectPage/components/FilesPanel/FilesPanel';
import NavbarActions from '@/ui/pages/projectPage/components/NavbarActions';
import ProjectHeader from '@/ui/pages/projectPage/components/ProjectHeader';
import { usePropertyPanelVisibility } from '@/ui/pages/projectPage/components/PropertyPanel/usePropertyPanelVisibility';
import ProjectOverviewCards from '@/ui/pages/projectPage/components/ProjectOverviewCards';
import ServicePlanPeriodHeader from '@/ui/pages/projectPage/components/ServicePlanPeriodHeader';
import SiteAddressCard from '@/ui/pages/projectPage/components/SiteAddressCard';
import SiteAddressModal from '@/ui/pages/projectPage/components/SiteAddressModal';
import TaskAccordion from '@/ui/pages/projectPage/components/TaskAccordion';
import { useHashFragment } from '@/utils/customHooks';
import AddCardButton from '@payaca/components/addCard/AddCardButton';

import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { DefaultSystemRoles } from '@payaca/permissions/default-role.config';
import { IntegratedEmailsPermissions } from '@payaca/permissions/integrated-emails/integrated-emails.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { LabourForecastPermissions } from '@payaca/permissions/labourForecast/labourForecast.permissions';
import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import { CustomFieldGroup } from '../../components/customFieldGroup/CustomFieldGroup';
import DesignPanel from './components/DesignPanel/DesignPanel';
import EstimatesPanel from './components/EstimatesPanel/EstimatesPanel';
import { LabourPanel } from './components/LabourPanel/LabourPanel';
import { MaterialsPanel } from './components/MaterialsPanel';
import PropertyPanel from './components/PropertyPanel/PropertyPanel';
import './ProjectPage.sass';

export const ProjectPageRedirect = () => {
  const { dealId, projectId } = useParams<{
    dealId?: string;
    projectId?: string;
  }>();
  const { project } = useGetProjectByIdOverview(dealId ?? projectId);
  const location = useLocation();
  const pathname = location.pathname;
  let endParams: string[] | undefined;
  if (dealId) {
    endParams = pathname.split(dealId);
  } else if (projectId) {
    endParams = pathname.split(projectId);
  }

  if (project?.publicId) {
    return (
      <Redirect
        to={`/projects/${project.publicId}${endParams ? endParams[1] : ''}`}
      />
    );
  }
};

export type Props = {
  projectId: string;
};
const ProjectPage: FC<Props> = ({ projectId }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const query = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const { project } = useGetProjectByIdOverview(projectId);
  const [showDealSiteAddressesModal, toggleDealSiteAddressesModal] =
    useHashFragment('#site-addresses');

  const [confirmPaymentId, setConfirmPaymentId] = useState<number>();

  const deal = useDeal(project?.id ? +project.id : undefined);

  const errorFetchingDeal = useSelector((state) => {
    return !!(
      project?.id &&
      state.deals?.deals &&
      state.deals.deals[+project.id]?.fetchFailedAt
    );
  });
  const jobs = useSelector((state) => {
    return project?.id ? getJobsByDealId(state, +project.id) : undefined;
  });

  const latestJob = jobs?.[0];
  const latestJobContentId = jobs?.[0]?.jobContentId;

  useEffect(() => {
    if (!!deal && query.new === 'true') {
      if (!deal?.siteAddresses?.length) {
        // new deal with no site addresses - open add site address modal
        history.push(`#site-addresses`);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'history'
  }, [query, deal]);

  useEffect(() => {
    dispatch(pipelineActions.requestGetAccountPipelines());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  useEffect(() => {
    if (!latestJobContentId) return;
    dispatch(
      // TODO why only do this for the main one? surely need for all?
      jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
        latestJobContentId
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [latestJobContentId]);

  useEffect(() => {
    if (project?.id) {
      dispatch(dealsActions.requestGetDeal(+project.id));
      dispatch(jobActions.requestGetJobsForDeal(+project.id));

      dispatch(jobContentActions.requestGetJobContentsForDeal(+project.id));

      dispatch(
        jobPaymentActions.requestGetJobPaymentsForDeal(
          +project.id,
          (freshJobPayments) => {
            const pendingPayments = getBacsPendingPayments(freshJobPayments);

            if (pendingPayments.length) {
              setConfirmPaymentId(pendingPayments[0].id);
            }
          }
        )
      );
      dispatch(
        scheduledEventActions.requestGetScheduledEventsForDeal(+project.id)
      );
      dispatch(documentActions.requestGetDocumentsForDeal(+project.id));
      dispatch(notesActions.getNotesForDeal.request({ dealId: +project.id }));

      // there is no way for deal JLIs to change without navigating away from the deal page, so only need to run this once and don't need to include this in the onDealUpdateSuccess function
      dispatch(jobContentActions.requestGetJobLineItemsForDeal(+project.id));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [project]);

  useEffect(() => {
    dispatch(customerActions.clearCurrentCustomer());
    if (userHasRequiredPermission([AccountsPermissions.GET_USERS])) {
      dispatch(usersActions.getAccountUsers());
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'userHasRequiredPermission', and 'dispatch'
  }, []);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(
        customerActions.requestGetAndSetCurrentCustomer(deal.customerId)
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal]);

  useEffect(() => {
    // redirect to "deals" page in the event we shouldn't be accessing this deal
    if (errorFetchingDeal) history.push('/deals');
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'history'
  }, [errorFetchingDeal]);
  const onDealUpdateSuccess = useCallback(
    (updatedJobIds?: number[]) => {
      if (!project?.id) return;
      dispatch(dealsActions.requestGetDeal(+project.id));
      if (updatedJobIds) {
        updatedJobIds.map((updatedJobId) => {
          dispatch(jobActions.requestGetJob(updatedJobId));
        });
      } else if (latestJob?.id) {
        dispatch(jobActions.requestGetJob(latestJob.id));
      }

      dispatch(jobPaymentActions.requestGetJobPaymentsForDeal(+project.id));
      dispatch(
        scheduledEventActions.requestGetScheduledEventsForDeal(+project.id)
      );
      dispatch(documentActions.requestGetDocumentsForDeal(+project.id));
      dispatch(notesActions.getNotesForDeal.request({ dealId: +project.id }));
      void queryClient.invalidateQueries({
        queryKey: projectKeys.project(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.profitBreakdown(projectId),
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'projectId', and 'dispatch'
    [project, latestJob, queryClient]
  );

  const { userHasRequiredPermission } = usePermissions();

  // The Restricted Sales Agent must not see the project Labour tab, but it holds the timelog
  // permissions the tab is gated on below, so no permission distinguishes it here. Gating on
  // the role is a deliberate stop-gap pending a cleaner permission.
  const { user } = useGetMe();
  const isRestrictedSalesAgent =
    user?.systemRole === DefaultSystemRoles.RESTRICTED_SALES_AGENT;

  const hasProtoInvoice = jobs?.some(
    (job) => job.isProtoInvoice && !job.archivedAt && !job.inactivatedAt
  );

  const hasEstimates = useMyAccountHasFeature('hasEstimates');
  const { hasVisibleContent: showPropertyTab } = usePropertyPanelVisibility();

  const translate = useTranslation();

  const defaultTabSlug =
    project?.type?.toUpperCase() === 'SERVICE' ? 'commitments' : 'activity';

  const isServiceProject = project?.type?.toUpperCase() === 'SERVICE';

  const routerTabPanels = useMemo(() => {
    const panels: TabPanel[] = [];
    if (!project) {
      return panels;
    }

    const canViewInvoices = userHasRequiredPermission([
      InvoicesPermissions.GET_INVOICES,
    ]);
    const canViewPayments = userHasRequiredPermission([
      PaymentsPermissions.GET_PAYMENTS,
    ]);

    if (isServiceProject) {
      panels.push({
        label: 'Commitments',
        slug: 'commitments',
        render: () => <CommitmentsPanel />,
      });
    }

    panels.push({
      label: 'Activity',
      slug: 'activity',
      render: () => <ActivityPanel />,
    });

    if (showPropertyTab) {
      panels.push({
        label: 'Property',
        slug: 'property',
        render: () => <PropertyPanel />,
      });
    }

    if (
      !isServiceProject &&
      (hasEstimates ||
        userHasRequiredPermission([DealsPermissions.GET_INTEGRATIONS]))
    ) {
      panels.push({
        label: 'Design',
        slug: 'design',
        render: () => <DesignPanel />,
      });
    }

    if (!isServiceProject && hasEstimates) {
      panels.push({
        label: 'Estimates',
        slug: 'estimates',
        render: () => <EstimatesPanel />,
      });
    }

    if (!isServiceProject && project.version > 1 && !hasProtoInvoice) {
      panels.push({
        label: 'Proposals',
        slug: 'proposals',
        render: () => (
          <DealProposals
            dealId={+project.id}
            projectPublicId={project.publicId}
            onDealUpdateSuccess={onDealUpdateSuccess}
          />
        ),
      });
    }

    if (!isServiceProject && project.version === 1) {
      panels.push({
        // Without invoice access (e.g. the Restricted Sales Agent) the tab carries only
        // proposals; the component hides its Invoices section on the same permission.
        label: canViewInvoices ? 'Proposals / invoices' : 'Proposals',
        slug: 'proposals-and-invoices',
        render: () => <DealProposalsInvoicesV1 dealId={+project.id} />,
      });
    }

    if (!isServiceProject && project.version > 1) {
      // The Invoices tab is hidden from roles without invoice access. The tab routing
      // stays version-based so a v2 project never falls through to the v1 Payments tab
      // below.
      if (canViewInvoices) {
        panels.push({
          label: 'Invoices',
          slug: 'invoices-and-payments',
          render: () => (
            <DealInvoicesPayments
              dealId={+project.id}
              onDealUpdateSuccess={onDealUpdateSuccess}
            />
          ),
        });
      }
    } else if (!isServiceProject && canViewPayments) {
      panels.push({
        label: 'Payments',
        slug: 'payments',
        render: () => (
          <DealPayments
            dealId={+project.id}
            onDealUpdateSuccess={onDealUpdateSuccess}
          />
        ),
      });
    }

    panels.push({
      label: 'Schedule',
      slug: 'schedule',
      render: () => (
        <DealScheduledEvents
          dealId={+project.id}
          onDealUpdateSuccess={onDealUpdateSuccess}
        />
      ),
    });

    if (!isServiceProject) {
      panels.push({
        label: 'Tasks',
        slug: 'tasks',
        // Keyed on the query string so a link carrying filters re-seeds them,
        // even when the tab is already open.
        render: () => (
          <DealTasks
            key={search}
            projectId={projectId}
            onDealUpdateSuccess={onDealUpdateSuccess}
          />
        ),
      });
    }

    panels.push({
      label: 'Files',
      slug: 'files',
      render: () => (
        <FilesPanel projectId={projectId} projectNumericalId={+project.id} />
      ),
    });

    if (
      userHasRequiredPermission([MaterialsListPermissions.GET_MATERIALS_LISTS])
    ) {
      panels.push({
        label: 'Materials',
        slug: 'materials',
        render: () => <MaterialsPanel dealId={+project.id} />,
      });
    }

    if (
      !isRestrictedSalesAgent &&
      (userHasRequiredPermission([
        TimelogsPermissions.GET_SELF_ASSIGNED_TIMELOGS,
      ]) ||
        userHasRequiredPermission([
          LabourForecastPermissions.GET_LABOUR_FORECAST,
        ]))
    ) {
      panels.push({
        label: 'Labour',
        slug: 'labour',
        render: () => <LabourPanel />,
      });
    }

    return panels;
  }, [
    project,
    hasProtoInvoice,
    hasEstimates,
    userHasRequiredPermission,
    isRestrictedSalesAgent,
    projectId,
    onDealUpdateSuccess,
    isServiceProject,
    showPropertyTab,
    search,
  ]);

  const siteAddressContacts = useMemo(() => {
    return (
      project?.addresses.site.reduce<Contact[]>((acc, site) => {
        acc.push(...site.contacts);

        return acc;
      }, []) || []
    );
  }, [project?.addresses.site]);

  return (
    <>
      <Helmet
        title={translate('pages.deal.title', {
          reference: project?.reference || '',
        })}
      />
      <AuthenticatedPageWrapper
        className="tabbed-content-page-wrapper deal-page"
        breadcrumbItems={
          project
            ? [
                isServiceProject
                  ? {
                      label: 'Service periods',
                      to: '/service-plans/service-periods',
                    }
                  : {
                      label: project.pipeline.name ?? 'Projects',
                      to: '/pipelines/' + project.pipeline.publicId,
                    },
                {
                  label: `Project: #${project.reference}`,
                },
              ]
            : []
        }
      >
        {deal?.archivedAt && (
          <PageBanner variant="warning">
            <Text as="span">
              {!project?.version || project.version < 2
                ? 'This project has been archived and cannot be restored.'
                : 'This project has been archived.'}
            </Text>
          </PageBanner>
        )}
        <div className="md:grid-template-desktop flex flex-col md:grid md:items-start">
          <RouterTabs panels={routerTabPanels}>
            <div className="grid-area-item1 p-6 pb-0 md:pb-4 md:pe-4">
              <Card>
                <Card.Body>
                  <ProjectHeader />

                  {isServiceProject ? (
                    <ServicePlanPeriodHeader />
                  ) : (
                    <ProjectOverviewCards />
                  )}

                  <RouterTabs.Nav
                    className="z-sticky sticky top-0 -mb-4 hidden items-end md:-mb-5 md:grid"
                    defaultTabSlug={defaultTabSlug}
                    navSuffix={
                      !project?.archivedAt ? (
                        <NavbarActions
                          className="py-4 md:pb-5"
                          setIsSendEmailModalOpen={setIsSendEmailModalOpen}
                        />
                      ) : undefined
                    }
                  />
                </Card.Body>
              </Card>
            </div>

            <div className="grid-area-item2 p-6 md:row-span-2 md:px-0 md:pe-4">
              <div className="h-full space-y-3.5 md:overflow-auto">
                {project?.addresses.site[0] ? (
                  <SiteAddressCard
                    address={project.addresses.site[0]}
                    furtherAddressCount={project.addresses.site.length - 1}
                    projectId={projectId}
                    onEditAddress={toggleDealSiteAddressesModal}
                  />
                ) : (
                  <AddCardButton
                    className="w-full"
                    onClick={toggleDealSiteAddressesModal}
                    text="Add site address"
                  />
                )}

                <Card>
                  <Card.Body>
                    <div className="space-y-3.5">
                      <AccordionWithChildCount
                        title="Customer contacts"
                        defaultOpen={true}
                        count={`${project?.customer?.contacts.length || 0}`}
                        chevronPosition="left"
                      >
                        {!project?.customer?.contacts.length && (
                          <p className="mt-2">
                            No customer contacts have been added
                          </p>
                        )}
                        {project?.customer?.contacts.map((contact) => {
                          return (
                            <ContactCard
                              key={contact.id}
                              variant="customerContact"
                              {...contact}
                            />
                          );
                        })}
                      </AccordionWithChildCount>
                      <AccordionWithChildCount
                        title="Access contacts"
                        count={`${siteAddressContacts.length}`}
                        chevronPosition="left"
                      >
                        {!siteAddressContacts.length && (
                          <p className="mt-2">
                            No access contacts have been added to this address
                          </p>
                        )}
                        {siteAddressContacts.map((contact) => {
                          return (
                            <ContactCard
                              key={contact.id}
                              variant="accessContact"
                              {...contact}
                            />
                          );
                        })}
                      </AccordionWithChildCount>
                    </div>
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Body>
                    <TaskAccordion />
                  </Card.Body>
                </Card>
                {project && (
                  <PermissionGuard
                    renderIfHasPermissions={[
                      AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES,
                    ]}
                  >
                    <CustomFieldGroup type="project" projectId={projectId} />
                  </PermissionGuard>
                )}
              </div>
            </div>

            <div className="grid-area-item3 p-6 pt-0 md:pe-4">
              {!project?.archivedAt && (
                <NavbarActions
                  className="mb-2 w-full justify-end md:hidden"
                  setIsSendEmailModalOpen={setIsSendEmailModalOpen}
                />
              )}
              <RouterTabs.Nav
                className="z-sticky sticky mb-4 md:hidden bg-white top-0 py-2"
                defaultTabSlug={defaultTabSlug}
              />

              <RouterTabs.Panels />
            </div>
          </RouterTabs>
        </div>
      </AuthenticatedPageWrapper>
      {!!confirmPaymentId && (
        <ConfirmPaymentModal
          jobPaymentId={confirmPaymentId}
          isOpen={!!confirmPaymentId}
          onClose={() => {
            setConfirmPaymentId(undefined);
          }}
          confirmPaymentCallback={() => {
            setConfirmPaymentId(undefined);
            onDealUpdateSuccess();
          }}
        />
      )}
      {project?.id && (
        <PermissionGuard
          renderIfHasPermissions={[IntegratedEmailsPermissions.SEND_EMAIL]}
        >
          <SendIntegratedEmailModal
            onClose={() => setIsSendEmailModalOpen(false)}
            isOpen={isSendEmailModalOpen}
            projectId={projectId}
          />
        </PermissionGuard>
      )}
      <SiteAddressModal
        isOpen={showDealSiteAddressesModal}
        onClose={toggleDealSiteAddressesModal}
        onDealUpdateSuccessLegacy={onDealUpdateSuccess}
      />
    </>
  );
};

export default ProjectPage;
